/**
 * Hooks del módulo interno de Certificación SII (platform admin, tenant-scoped).
 *
 * API en /api/v1/certification/. Flujo: ingestar set → cargar CAFs → receptor +
 * mapeo ítem→producto → generar → enviar → consultar → resultado → resolución.
 * Más el intercambio (Ley 19.983) como acción aparte.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient, { apiDownload } from '@/lib/apiClient'

// ── Tipos ──────────────────────────────────────────────────────────────────

export interface CertItem {
  nombre?: string | null
  exento?: boolean | null
  cantidad?: string | null
  precio_unitario?: string | null
  valor_linea?: string | null
  descuento_pct?: string | null
  unidad?: string | null
}

export interface CertItemProduct {
  nombre?: string
  precio_unitario?: string
  exento?: boolean
  // Solo para la clave especial '_glosa' (NC/ND que corrige texto): descripción larga
  // que va en DscItem (p. ej. "DONDE DICE: … / DEBE DECIR: …").
  descripcion?: string
}

export interface CertCase {
  id: string
  case_number: string
  sequence: number
  document_type: string
  document_type_label: string
  attributes: Record<string, unknown>
  items: CertItem[]
  item_products: Record<string, CertItemProduct>
  folio: number | null
  case_status: string
  case_status_display: string
  case_result: unknown
}

export interface CertCaf {
  id: string
  document_type: string
  folio_desde: number
  folio_hasta: number
  next_folio: number
  created_at: string
}

export interface CertRunList {
  id: string
  set_type: string
  numero_atencion: string
  environment: string
  status: string
  status_display: string
  result_status: string
  result_status_display: string
  track_id: string
  sent_at: string | null
  resolution_number: string
  resolution_date: string | null
  cases_count: number
  cafs_count: number
  created_at: string
  updated_at: string
}

export interface CertRunDetail extends CertRunList {
  instructions: string
  notes: string
  result_notes: string
  receptor_rut: string
  receptor_legal_name: string
  receptor_giro: string
  receptor_address: string
  receptor_commune: string
  result_raw: Record<string, unknown> | null
  setdte_id: string
  has_signed_xml: boolean
  cases: CertCase[]
  cafs: CertCaf[]
}

export interface ReceptorInput {
  receptor_rut?: string
  receptor_legal_name?: string
  receptor_giro?: string
  receptor_address?: string
  receptor_commune?: string
}

export interface ResolutionInput {
  resolution_number: string
  resolution_date: string
  activate_dte_types: boolean
  set_production: boolean
}

// ── Query keys ─────────────────────────────────────────────────────────────

const keys = {
  all: ['certification'] as const,
  runs: () => ['certification', 'runs'] as const,
  run: (id: string) => ['certification', 'run', id] as const,
}

// ── Queries ──────────────────────────────────────────────────────────────────

export function useCertRuns() {
  return useQuery({
    queryKey: keys.runs(),
    queryFn: async () => {
      const { data } = await apiClient.get<{ results: CertRunList[]; count: number }>(
        '/certification/runs/',
      )
      return data
    },
  })
}

export function useCertRun(id: string | undefined) {
  return useQuery({
    queryKey: keys.run(id || ''),
    queryFn: async () => {
      const { data } = await apiClient.get<CertRunDetail>(`/certification/runs/${id}/`)
      return data
    },
    enabled: !!id,
  })
}

// ── Mutaciones sobre la lista ──────────────────────────────────────────────

export function useIngestSet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (raw_set_txt: string) => {
      const { data } = await apiClient.post<{ results: CertRunList[]; count: number }>(
        '/certification/runs/', { raw_set_txt },
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.runs() }),
  })
}

export function useDeleteRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/certification/runs/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.runs() }),
  })
}

// ── Mutaciones sobre un run (invalidan su detalle + la lista) ──────────────

function useRunMutation<TInput>(
  runId: string,
  fn: (input: TInput) => Promise<CertRunDetail | unknown>,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.run(runId) })
      qc.invalidateQueries({ queryKey: keys.runs() })
    },
  })
}

export function useUpdateReceptor(runId: string) {
  return useRunMutation<ReceptorInput>(runId, async (input) => {
    const { data } = await apiClient.patch(`/certification/runs/${runId}/receptor/`, input)
    return data
  })
}

export function useUploadCaf(runId: string) {
  return useRunMutation<string>(runId, async (caf_xml) => {
    const { data } = await apiClient.post(`/certification/runs/${runId}/cafs/`, { caf_xml })
    return data
  })
}

export function useDeleteCaf(runId: string) {
  return useRunMutation<string>(runId, async (cafId) => {
    await apiClient.delete(`/certification/runs/${runId}/cafs/${cafId}/`)
  })
}

export function useUpdateCaseMapping(runId: string) {
  return useRunMutation<{ caseId: string; item_products?: Record<string, CertItemProduct>; document_type?: string }>(
    runId,
    async ({ caseId, ...body }) => {
      const { data } = await apiClient.patch(`/certification/runs/${runId}/cases/${caseId}/`, body)
      return data
    },
  )
}

export function useGenerateRun(runId: string) {
  return useRunMutation<void>(runId, async () => {
    const { data } = await apiClient.post(`/certification/runs/${runId}/generate/`, {})
    return data
  })
}

export function useSendRun(runId: string) {
  return useRunMutation<void>(runId, async () => {
    const { data } = await apiClient.post(`/certification/runs/${runId}/send/`, {})
    return data
  })
}

export function useQueryRun(runId: string) {
  return useRunMutation<void>(runId, async () => {
    const { data } = await apiClient.post(`/certification/runs/${runId}/query/`, {})
    return data
  })
}

export function useRegisterResult(runId: string) {
  return useRunMutation<{ result_status: string; result_notes: string }>(runId, async (input) => {
    const { data } = await apiClient.post(`/certification/runs/${runId}/result/`, input)
    return data
  })
}

export function useApplyResolution(runId: string) {
  return useRunMutation<ResolutionInput>(runId, async (input) => {
    const { data } = await apiClient.post(`/certification/runs/${runId}/resolution/`, input)
    return data
  })
}

// ── Descargas (blob autenticado) ───────────────────────────────────────────

export function downloadRunXml(runId: string, numeroAtencion: string) {
  return apiDownload(`/certification/runs/${runId}/xml/`, `set_${numeroAtencion || 'cert'}.xml`)
}

export function downloadCasePdf(runId: string, caseId: string, caseNumber: string) {
  return apiDownload(`/certification/runs/${runId}/cases/${caseId}/pdf/`, `caso_${caseNumber}.pdf`)
}

// ── Intercambio (Ley 19.983): sube EnvioDTE de muestra → descarga respuesta firmada ──

export interface IntercambioInput {
  envio_xml: string
  id_respuesta: number
  nmb_envio?: string
}

async function blobErrorMessage(blob: Blob): Promise<string> {
  try {
    const text = await blob.text()
    const json = JSON.parse(text)
    return json?.error?.message || 'No se pudo generar la respuesta de intercambio.'
  } catch {
    return 'No se pudo generar la respuesta de intercambio.'
  }
}

export function useIntercambio() {
  return useMutation({
    mutationFn: async (input: IntercambioInput) => {
      try {
        const res = await apiClient.post('/certification/intercambio/', input, {
          responseType: 'blob',
        })
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `respuesta_intercambio_${input.id_respuesta}.xml`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err: unknown) {
        // El cuerpo del error también viene como blob (responseType=blob).
        const e = err as { response?: { data?: Blob } }
        if (e.response?.data instanceof Blob) {
          throw new Error(await blobErrorMessage(e.response.data))
        }
        throw err
      }
    },
  })
}
